import * as React from "react";
import {Header, Modal, Segment, Label, Icon, Message, Button} from "semantic-ui-react";
import {Splitter} from "../../../../util/table-splitter-ui/src/components/Splitters/Splitter";
import AceEditor from "react-ace";
import {QueuedEvent} from "../../../../../generated";
import {QueuedEventsTable} from "../../queues/events/QueuedEventsTable";

export interface QueuedEventSelectorProps {
    isOpen: boolean
    onClose: () => void
    queuedEventIds: string[] // Changed from transportOrderIds
    setQueuedEventIds: (queuedEventIds: string[]) => void // Changed from setTransportOrderIds
    onQueuedEventsSelected: (queuedEvents: QueuedEvent[]) => void
    allowMultipleSelection?: boolean
}

export function QueuedEventSelector(props: React.PropsWithChildren<QueuedEventSelectorProps>) {
    const [selectedQueuedEventStr, setSelectedQueuedEventStr] = React.useState<string | null>(null)
    const [selectedQueuedEvents, setSelectedQueuedEvents] = React.useState<QueuedEvent[]>([])

    const canProcessEvent = (event: QueuedEvent): boolean => {
        if (event.transportOrderEvent) return true
        if (event.etaEvent) return true
        return false
    }

    const getEventTypeLabel = (event: QueuedEvent): string => {
        if (event.etaEvent) return 'ETA_EVENT'
        if (event.transportOrderEvent?.change?.type?.toString().includes('ATTACHMENT')) return 'ATTACHMENT_EVENT'
        if (event.transportOrderEvent) return 'TRANSPORT_ORDER_STATUS_EVENT'
        return 'UNSUPPORTED'
    }

    const handleSelectionChanged = (newSelected: QueuedEvent[]) => {
        const processableEvents = newSelected.filter(event => canProcessEvent(event))
        setSelectedQueuedEvents(processableEvents)
    }

    const handleClickedEvent = (event: QueuedEvent) => {
        setSelectedQueuedEventStr(JSON.stringify(event, null, 4))
    }

    const handleClose = () => {
        if (selectedQueuedEvents.length > 0) {
            const newQueuedEventIds = selectedQueuedEvents.map(event => event._id)

            // Filter out any IDs that are already selected to avoid duplicates
            const existingIds = props.queuedEventIds.filter(id => !newQueuedEventIds.includes(id))
            const updatedIds = [...existingIds, ...newQueuedEventIds]

            props.setQueuedEventIds(updatedIds) // Updated to use queuedEventIds
            props.onQueuedEventsSelected(selectedQueuedEvents)
        }
        setSelectedQueuedEvents([])
        setSelectedQueuedEventStr(null)
        props.onClose()
    }

    React.useEffect(() => {
        if (!props.isOpen) {
            setSelectedQueuedEvents([])
            setSelectedQueuedEventStr(null)
        }
    }, [props.isOpen])

    return <Modal
        id='QueuedEventSelectorModal'
        open={props.isOpen}
        onClose={handleClose}
        size='fullscreen'
        closeIcon
    >
        <Header icon='list' content='Select Queued Events'/>
        <Modal.Content>
            <Message info style={{ display: 'flex', alignItems: 'center' }}>
                <Message.Header style={{ marginRight: 8 }}>Supported Event Types</Message.Header>
                <p>Only Transport Order Status Events, ETA Events, and Attachment Events can be processed.</p>
            </Message>

            {selectedQueuedEvents.length > 0 && (
                <Segment color="green" style={{ display: 'flex', alignItems: 'center' }}>
                    <Header as="h5" style={{ marginRight: '10px', marginBottom: 0 }}>
                        Selected Events ({selectedQueuedEvents.length}):
                    </Header>
                    {selectedQueuedEvents.map((event) => (
                        <Label key={event._id} color="green" style={{ marginBottom: 0 }}>
                            {event._id} ({getEventTypeLabel(event)})
                        </Label>
                    ))}
                </Segment>
            )}

            <Splitter
                name={"AiScripterQueuedEventSelectionModal"}
                left={
                    <QueuedEventsTable
                        onClickedEvent={handleClickedEvent}
                        onSelectionChanged={handleSelectionChanged}
                        history={history}
                    />
                }
                right={
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                        <AceEditor
                            mode="json"
                            theme="monokai"
                            name="queuedEventSelectionEditor"
                            editorProps={{$blockScrolling: true}}
                            value={selectedQueuedEventStr || ""}
                            width={"100%"}
                            height={"100%"}
                            readOnly={true}
                            placeholder="Select queued events from the table to view details here"
                        />
                    </div>
                }
            />
        </Modal.Content>

        <Modal.Actions>
            <Button onClick={handleClose}>
                <Icon name='checkmark' />
                {selectedQueuedEvents.length > 0 ? `Select ${selectedQueuedEvents.length} Events` : 'Close'}
            </Button>
        </Modal.Actions>
    </Modal>
}